// src/utils/errorHandler.ts
import { App as AntApp, message, notification } from "antd";
import type { ApiResponse } from "types";

let messageInstance: ReturnType<typeof AntApp.useApp> | null = null;
/**
 * 设置 App 实例（在应用启动时调用）
 */
export function setAppInstance(app: ReturnType<typeof AntApp.useApp>): void {
  messageInstance = app;
}
/**
 * 获取 message 实例
 */
function getMessageInstance() {
  return messageInstance?.message;
}

/**
 * 获取 notification 实例
 */
function getNotificationInstance() {
  return messageInstance?.notification;
}

/**
 * 清除认证信息
 */
export function clearAuthInfo(): void {
  // 清除 localStorage
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("rememberedEmail");

  // 清除 sessionStorage
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("user");

  // 清除 Cookie
  clearCookie("token");
  clearCookie("Authorization");
  clearCookie("user_id");
}

/**
 * 清除指定的 Cookie
 */
function clearCookie(name: string): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
}

/**
 * 处理 Token 过期
 */
export function handleTokenExpired(): void {
  clearAuthInfo();
  message.error("登录已过期，请重新登录");

  // 延迟跳转，让用户看到提示
  // setTimeout(() => {
  //   window.location.href = "/user/login";
  // }, 1000);
}

/**
 * 处理 API 错误响应
 */
export function handleErrorResponse(error: ApiResponse | any): void {
  // 安全地获取错误信息
  const errorMessage = error?.errorMessage || error?.message || "请求失败";
  const errorCode = error?.errorCode || error?.code;
  const showType = error?.showType ?? 2;
  const traceId = error?.traceId;

  if (!errorMessage) return;

  // ============ 检查 Token 过期 ============
  if (
    errorCode === "UNAUTHORIZED" ||
    errorCode === "TOKEN_EXPIRED" ||
    errorCode === "INVALID_TOKEN" ||
    errorMessage.includes("token") ||
    errorMessage.includes("unauthorized") ||
    errorMessage.includes("Unauthorized") ||
    errorMessage.includes("expired")
  ) {
    handleTokenExpired();
    return;
  }

  const errorInfo = traceId
    ? `${errorMessage} (TraceId: ${traceId})`
    : errorMessage;
  const msg = getMessageInstance();
  const notification = getNotificationInstance();

  switch (showType) {
    case 0: // silent
      break;
    case 1: // message.warning
      message.warning(errorInfo);
      break;
    case 2: // message.error
      message.error(errorInfo);
      break;
    case 4: // notification
      if (notification) {
        notification.error({
          message: "请求失败",
          description: errorInfo,
          duration: 4.5,
        });
      }
      break;
    case 9: // page
      window.location.href = `/exception?message=${encodeURIComponent(
        errorInfo
      )}`;
      break;
    default:
      message.error(errorInfo);
  }
}

/**
 * 处理 HTTP 状态码错误
 */
export function handleHttpError(status: number): void {
  const errorMessages: Record<number, string> = {
    400: "请求参数错误",
    401: "未授权，请重新登录",
    403: "禁止访问",
    404: "请求的资源不存在",
    500: "服务器内部错误",
    502: "网关错误",
    503: "服务暂时不可用",
    504: "网关超时",
  };

  const errorMessage = errorMessages[status] || `请求失败 (${status})`;

  // ============ 处理 401 未授权 ============
  if (status === 401) {
    handleTokenExpired();
    return;
  }
  const msg = getMessageInstance();
  const notification = getNotificationInstance();
  if (status === 403) {
    window.location.href = "/exception?status=403";
  } else if (status >= 500) {
    if (notification) {
      notification.error({
        message: "服务器错误",
        description: errorMessage,
      });
    }
  } else {
    if (msg) {
      msg.error(errorMessage);
    }
  }
}

/**
 * 显示成功消息
 */
export function showSuccessMessage(msg: string): void {
  // const message = getMessageInstance();
  // if (message) {
    message.success(msg);
  // }
}

/**
 * 显示信息消息
 */
export function showInfoMessage(msg: string): void {
  const message = getMessageInstance();
  if (message) {
    message.info(msg);
  }
}

/**
 * 显示警告消息
 */
export function showWarningMessage(msg: string): void {
  const message = getMessageInstance();
  if (message) {
    message.warning(msg);
  }
}

/**
 * 显示加载消息
 */
export function showLoadingMessage(msg: string): () => void {
  const message = getMessageInstance();
  if (message) {
    return message.loading(msg);
  }
  return () => {};
}

/**
 * 显示错误通知
 */
export function showErrorNotification(
  title: string,
  description: string
): void {
  const notification = getNotificationInstance();
  if (notification) {
    notification.error({
      message: title,
      description,
      duration: 4.5,
    });
  }
}

/**
 * 显示成功通知
 */
export function showSuccessNotification(
  title: string,
  description: string
): void {
  const notification = getNotificationInstance();
  if (notification) {
    notification.success({
      message: title,
      description,
      duration: 4.5,
    });
  }
}
